import type { UseQueryOptions } from '@tanstack/react-query';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import type { BffSubscription } from '@/core/models/bff-subscriptions';
import useAuthSession from '@/core/hooks/use-auth-session';
import parseLocale from '@/core/i18n/parse-locale';
import { getBffSubscriptions } from '@/core/services/fetchers';

/** Variables kept for API compatibility; BFF uses JWT, so userId only used for enabled */
export type Variables = {
  userId?: string;
};

/** Prefix-only key for invalidating every subscriptions query (avoids `undefined` segment mismatches). */
export const queryKeyPrefix = ['bff-subscriptions'] as const;

export const queryKey = (variables?: Variables) =>
  variables?.userId
    ? ([...queryKeyPrefix, variables.userId] as const)
    : queryKeyPrefix;

export const queryFn = async (
  languageCode: string,
): Promise<BffSubscription[]> => {
  const response = await getBffSubscriptions(languageCode);
  return response.data ?? [];
};

const useBffSubscriptionsQuery = (
  variables: Variables,
  options?: Omit<UseQueryOptions<BffSubscription[]>, 'queryKey' | 'queryFn'>,
) => {
  const locale = useLocale();
  const { language: languageCode } = parseLocale(locale);
  const hasAuthSession = useAuthSession();

  return useQuery({
    ...options,
    queryKey: queryKey(variables),
    queryFn:
      variables.userId && hasAuthSession
        ? () => queryFn(languageCode)
        : skipToken,
  });
};

export default useBffSubscriptionsQuery;
